import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import sm from './SpecialModeView.module.scss'
import styles from './KoreanSoundsView.module.scss'
import { speak, speakTracked, stopSpeaking } from '../speech/speak'
import useSpeechToText from '../speech/useSpeechToText'
import { celebrate, shake } from './juice'
import { cueCorrect, cueWrong, cueComplete } from './cues'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import MicIcon from '@mui/icons-material/Mic'
import SkipNextIcon from '@mui/icons-material/SkipNext'

// ─────────────────────────────────────────────────────────────────────────────
// "Sound it out" — the language-agnostic drill. The goal is to get a learner
// reading and SAYING words fast, not pronouncing them perfectly. A language
// plugs in how a word breaks into syllables and letters, how to draw a
// syllable, what to say for one letter, how to time the sweep, and how to
// score what the recogniser heard:
//   • Listen: the voice says the word and the letters light in reading order,
//     timed to it. Tap a letter to hear its sound, a syllable to hear it whole.
//   • Say it: what the recogniser hears is matched back onto the letters —
//     green for a hit, amber for a near miss, red for not heard. A recognisable
//     word passes; the score shows which sounds landed, it never gates.
//
// plugin: {
//   title, langCode, voiceURI, cps,
//   decompose(item) → syllables [{ text|ch, parts: [{ role, text|jamo, rom, … }] }],
//   align(item, transcript) → { syllables: [{ heard, parts: [{ score }] , score }], total },
//   soundOf(part, syl) → text to speak, partLabel(part) → label, partText(part) → glyph,
//   Block ({ syl, states, size, onPart }), timing(syl, rate) → ms per part,
//   pronunciation(syls, item) → string | '', legend → string | null, note → string,
//   blockSize(nSyllables) → px,
// }
// ─────────────────────────────────────────────────────────────────────────────

const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }
const PASS = 0.55

export default function SoundOutDrill({ plugin, items, voiceSpeed, onBack, onClose }) {
  const P = plugin
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 0.9
  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })

  const [idx, setIdx] = useState(0)
  const [stage, setStage] = useState('listen') // listen | say | done
  const item = items[idx]
  const syls = useMemo(() => (item ? P.decompose(item) : []), [item, P])
  const pron = useMemo(() => (item && P.pronunciation ? P.pronunciation(syls, item) : ''), [syls, item, P])

  const [states, setStates] = useState({}) // { [sylIndex]: { [partIndex]: state } }
  const [result, setResult] = useState(null)
  const [tip, setTip] = useState('')
  const scoresRef = useRef({})
  const wordRef = useRef(null)
  const timers = useRef([])
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  // The play in flight, so a play we cut short is not measured (see hear()).
  const playRef = useRef(null)
  const stopAll = () => { if (playRef.current) playRef.current.aborted = true; stopSpeaking() }
  useEffect(() => () => { clearTimers(); stopAll() }, [])

  const setPart = (si, pi, st) => setStates(s => ({ ...s, [si]: { ...(s[si] || {}), [pi]: st } }))

  // ── Listen ─────────────────────────────────────────────────────────────────
  // Web Speech gives no per-letter timing (a word boundary at best). The
  // plugin's timing model says how the letters SHARE a word's time; how long
  // the word takes is measured: each play reports when the voice starts and
  // ends, and the next play of that word (or the next word, from a running
  // per-syllable average) is scaled to fit. The first-ever play runs on the
  // model's guess alone.
  // `mult` scales the speed: 1 is the learner's voice speed, 0.5 is "slow" —
  // the voice and the sweep slow down together.
  const durRef = useRef({})  // `${word}|${mult}` → measured ms
  const calRef = useRef({})  // mult → measured ms per syllable (running average)
  const hear = useCallback((mult = 1) => {
    if (!item) return
    const m = (typeof mult === 'number' && mult > 0) ? mult : 1
    const r = rate * m
    clearTimers(); stopAll(); setStates({}); setResult(null); setTip('')
    const key = `${item.k}|${m}`
    const plan = syls.map(syl => P.timing(syl, r))
    const est = plan.flat().reduce((a, b) => a + b, 0)
    const n = syls.length || 1
    const target = durRef.current[key] || (calRef.current[m] ? calRef.current[m] * n : est)
    const scale = est > 0 ? target / est : 1
    const play = { aborted: false, t0: 0 }
    playRef.current = play
    speakTracked(item.k, P.voiceURI, r, {
      cps: P.cps,
      onStart: () => {
        play.t0 = performance.now()
        let t = 0
        syls.forEach((syl, si) => {
          plan[si].forEach((d0, pi) => {
            const at = t
            const d = (d0 || 0) * scale
            timers.current.push(setTimeout(() => setPart(si, pi, 'lit'), at))
            timers.current.push(setTimeout(() => setPart(si, pi, 'hit'), at + d))
            t += d
          })
        })
        timers.current.push(setTimeout(() => setStates({}), t + 700))
      },
      onEnd: () => {
        if (play.aborted || !play.t0) return
        const ms = performance.now() - play.t0
        if (ms < 80) return
        durRef.current[key] = ms
        const per = ms / n
        calRef.current[m] = calRef.current[m] ? calRef.current[m] * 0.5 + per * 0.5 : per
      },
    })
  }, [item, syls, rate, P])

  useEffect(() => {
    clearTimers(); setStates({}); setResult(null); setTip(''); setStage('listen')
    const t = setTimeout(() => hear(1), 400)
    return () => clearTimeout(t)
  }, [hear])

  const sayPart = (si, pi) => {
    const syl = syls[si]; const p = syl && syl.parts[pi]
    if (!p) return
    clearTimers(); stopAll(); setStates({ [si]: { [pi]: 'lit' } }); setTip(P.partLabel(p, syl))
    speak(P.soundOf(p, syl), P.voiceURI, rate)
    timers.current.push(setTimeout(() => setStates({}), 900))
  }
  const saySyllable = (si) => {
    const syl = syls[si]
    if (!syl || !syl.parts.length) return
    clearTimers(); stopAll(); setStates({ [si]: Object.fromEntries(syl.parts.map((_, pi) => [pi, 'lit'])) })
    setTip(P.syllableLabel ? P.syllableLabel(syl) : syl.text)
    speak(syl.text, P.voiceURI, rate)
    timers.current.push(setTimeout(() => setStates({}), 900))
  }

  // ── Say it ─────────────────────────────────────────────────────────────────
  const applyAlign = useCallback((transcript, final) => {
    if (!item) return null
    const r = P.align(item, transcript)
    const st = {}
    r.syllables.forEach((s, si) => {
      st[si] = {}
      s.parts.forEach((p, pi) => { st[si][pi] = p.score >= 1 ? 'hit' : p.score > 0 ? 'near' : (final ? 'miss' : 'idle') })
    })
    setStates(st)
    return r
  }, [item, P])

  const sttRef = useRef(null)
  const stt = useSpeechToText(P.langCode, {
    onFinal: (t) => {
      const r = applyAlign(t, true)
      if (!r) return
      setResult({ ...r, heard: t })
      scoresRef.current[idx] = Math.max(scoresRef.current[idx] || 0, r.total)
      if (r.total >= PASS) { cueCorrect(); celebrate(window.innerWidth / 2, window.innerHeight / 3, { count: 24, spread: 160 }) }
      else { cueWrong(); shake(wordRef.current) }
      if (sttRef.current) sttRef.current.stop()
    },
  })
  sttRef.current = stt
  useEffect(() => {
    if (stage === 'say' && stt.listening && stt.interim) applyAlign(stt.interim, false)
  }, [stt.interim, stt.listening, stage, applyAlign])

  const startSay = () => {
    if (stt.listening) { stt.stop(); return }
    clearTimers(); stopAll(); setStates({}); setResult(null); setTip('')
    setStage('say')
    stt.start()
  }
  const selfMark = () => { setResult({ total: 1, heard: '', self: true, syllables: [] }); scoresRef.current[idx] = 1; cueCorrect() }
  const next = () => {
    stopAll(); clearTimers()
    if (stt.listening) stt.stop()
    if (idx + 1 >= items.length) { setStage('done'); cueComplete() }
    else setIdx(i => i + 1)
  }
  const again = () => { scoresRef.current = {}; setIdx(0); setStage('listen') }

  const root = `${sm.view} ${theme === 'dark' ? sm.dark : sm.light}`
  const chrome = (
    <>
      <button className={sm.themeBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
        {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </button>
      <button className={sm.closeBtn} onClick={onClose} title="Close"><CloseIcon /></button>
    </>
  )

  if (!item) {
    return (
      <div className={root}>{chrome}
        <div className={styles.wrap}><div className={styles.muted}>No words to practise — pick a syllable count or type some words.</div>
          <div className={styles.actions}><button className={styles.btn} onClick={onBack}>← Setup</button></div>
        </div>
      </div>
    )
  }

  if (stage === 'done') {
    const scored = items.map((w, i) => ({ ...w, s: scoresRef.current[i] }))
    const said = scored.filter(w => w.s != null)
    const avg = said.length ? said.reduce((a, w) => a + w.s, 0) / said.length : 0
    return (
      <div className={root}>{chrome}
        <div className={styles.wrap}>
          <div className={styles.label}>{P.title}</div>
          <h2 className={styles.title}>{said.length ? `${Math.round(avg * 100)}% of the sounds landed` : 'Round done'}</h2>
          <p className={styles.muted}>{said.length} of {items.length} words said out loud. Amber and red letters are the sounds to listen for next time — not mistakes to drill.</p>
          <div className={styles.summary}>
            {scored.map((w, i) => (
              <div key={i} className={styles.sumRow}>
                <span className={styles.sumWord}>{w.k}</span>
                <span className={styles.sumMeaning}>{w.e}</span>
                <span className={`${styles.sumScore} ${w.s == null ? styles.sumSkip : w.s >= PASS ? styles.sumGood : styles.sumMeh}`}>{w.s == null ? 'skipped' : `${Math.round(w.s * 100)}%`}</span>
              </div>
            ))}
          </div>
          <div className={styles.actions}>
            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={again}>Again</button>
            <button className={styles.btn} onClick={onBack}>← Setup</button>
          </div>
        </div>
      </div>
    )
  }

  const Block = P.Block
  const size = P.blockSize(syls.length)
  const stateOf = (si, pi) => (states[si] && states[si][pi]) || 'idle'
  // "Heard X" only when it really differs — not for the recogniser's capital letter.
  const norm = P.norm || ((s) => String(s || ''))
  const differs = (a, b) => norm(a) !== norm(b)
  return (
    <div className={root}>{chrome}
      <div className={styles.wrap}>
        <div className={styles.top}>
          <button className={styles.linkBtn} onClick={onBack}>← Setup</button>
          <span className={styles.label}>{P.title}</span>
          <span className={styles.progress}>{idx + 1} / {items.length}</span>
        </div>

        {/* the word, syllable by syllable, letter by letter */}
        <div className={styles.blocks} ref={wordRef}>
          {syls.map((syl, si) => (
            <React.Fragment key={si}>
              {syl.wordBreak ? <span className={styles.wordGap} /> : null}
              <div className={styles.sylCol}>
                <Block syl={syl} states={syl.parts.map((_, pi) => stateOf(si, pi))} size={size} onPart={(pi) => sayPart(si, pi)} />
                <button className={styles.sylBtn} onClick={() => saySyllable(si)} title="Hear this syllable">
                  {syl.text}
                  {result && !result.self && result.syllables[si]?.heard && differs(result.syllables[si].heard, syl.text)
                    ? <span className={styles.heardAs}>heard {result.syllables[si].heard}</span> : null}
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* the sounds in order, coloured with the blocks */}
        <div className={styles.strip}>
          {syls.map((syl, si) => (
            <span key={si} className={`${styles.stripSyl} ${syl.wordBreak ? styles.stripSylBreak : ''}`}>
              {syl.parts.map((p, pi) => (
                <button key={pi} className={`${styles.stripPart} ${styles['st_' + stateOf(si, pi)]}`} onClick={() => sayPart(si, pi)}>
                  <span className={styles.stripJamo}>{P.partText(p)}</span>
                  <span className={styles.stripRom}>{p.rom || '–'}</span>
                </button>
              ))}
            </span>
          ))}
        </div>

        {/* as printed */}
        <div className={styles.wordRow}>
          <span className={styles.word}>{item.k}</span>
          {pron ? <span className={styles.rom}>{pron}</span> : null}
          {item.e ? <span className={styles.meaning}>{item.e}</span> : null}
        </div>
        {P.legend ? <div className={styles.muted}>{typeof P.legend === 'function' ? P.legend(syls, item) : P.legend}</div> : null}
        {tip ? <div className={styles.tip}>{tip}</div> : null}

        <div className={styles.actions}>
          <button className={styles.btn} onClick={() => hear(1)}><VolumeUpIcon fontSize="small" /> Hear it</button>
          <button className={styles.btn} onClick={() => hear(0.5)} title="Half speed — the letters light up slowly too"><VolumeUpIcon fontSize="small" /> Slow</button>
          {stt.supported
            ? <button className={`${styles.btn} ${styles.btnPrimary} ${stt.listening ? styles.btnLive : ''}`} onClick={startSay}>
                <MicIcon fontSize="small" /> {stt.listening ? 'Listening… tap to stop' : 'Say it'}
              </button>
            : <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={selfMark}>I said it ✓</button>}
          <button className={styles.btn} onClick={next}>{idx + 1 >= items.length ? 'Finish' : 'Next'} <SkipNextIcon fontSize="small" /></button>
        </div>

        {stt.listening && stt.interim ? <div className={styles.heard}>{stt.interim}…</div> : null}
        {result && !result.self && (
          <div className={`${styles.verdict} ${result.total >= PASS ? styles.verdictGood : styles.verdictMeh}`}>
            {result.total >= PASS
              ? `That's the word — ${Math.round(result.total * 100)}% of the sounds landed.`
              : `Close — ${Math.round(result.total * 100)}%. The red letters are the sounds I didn't catch; amber were nearly there.`}
            <span className={styles.heardLine}>I heard: {result.heard || '…'}</span>
          </div>
        )}
        {stt.error ? <div className={styles.err}>{stt.error}</div> : null}
        {!stt.supported && <div className={styles.muted}>Speech recognition isn't available in this browser, so mark each word yourself.</div>}

        <p className={styles.note}>{P.note}</p>
      </div>
    </div>
  )
}
